/**
 * A conversa do caso entre o cliente e a equipe (E149 — GUS-149, ADR-0102 §3).
 *
 * Espelha `apps/api/types/conversa_do_caso.py` (a paridade é conferida por
 * `tests/unit/test_e149_f2_contrato_web.py`). O servidor decide o lado de quem escreve, a
 * situação de cada item e se o caso já tem entrega; a tela só oferece o que cabe ao lado e traduz
 * a recusa pelo código. A conversa não aprova, não reemite e não calcula prazo: o "responder até"
 * é o que a equipe informou.
 */
import axios from 'axios';
import type { AxiosInstance } from 'axios';

export type LadoDaConversa = 'cliente' | 'equipe';
export type TipoDaMensagem =
  | 'mensagem'
  | 'pedido_de_material'
  | 'resposta_ao_pedido'
  | 'complemento'
  | 'questionamento'
  | 'resposta_ao_questionamento'
  | 'apontamento'
  | 'resposta_ao_apontamento'
  | 'ciencia';
export type DesfechoDaResposta =
  | 'atendido'
  | 'sem_documento'
  | 'respondido'
  | 'nova_versao'
  | 'resolvido';
export type SituacaoDoItem =
  | 'aberto'
  | 'atendido'
  | 'sem_documento'
  | 'respondido'
  | 'nova_versao'
  | 'resolvido';

export interface MensagemData {
  id: string;
  lado: LadoDaConversa;
  tipo: TipoDaMensagem;
  autor: string | null;
  texto: string | null;
  responde_a: string | null;
  desfecho: DesfechoDaResposta | null;
  material: string | null;
  motivo: string | null;
  responder_ate: string | null;
  recebimento_id: string | null;
  origem: string | null;
  criada_em: string;
  situacao: SituacaoDoItem | null;
}

export interface PendenciasDaConversaData {
  pedidos_abertos: number;
  questionamentos_abertos: number;
  apontamentos_abertos: number;
}

export interface ConversaDoCasoData {
  lado_do_leitor: LadoDaConversa;
  entregue: boolean;
  envia_documento: boolean;
  mensagens: MensagemData[];
  pendencias: PendenciasDaConversaData;
}

/** O corpo do `POST`: o que cada tipo exige é conferido pelo servidor. */
export interface MensagemEntradaData {
  tipo: TipoDaMensagem;
  texto?: string | null;
  responde_a?: string | null;
  desfecho?: DesfechoDaResposta | null;
  material?: string | null;
  motivo?: string | null;
  responder_ate?: string | null;
  recebimento_id?: string | null;
  origem?: string | null;
}

/** Os limites de cada campo, os mesmos do servidor. */
export const LIMITES_DA_CONVERSA = {
  texto: 4000,
  material: 500,
  motivo: 1000,
  origem: 500,
} as const;

/** O tipo que cada resposta responde: a situação do item sai das respostas. */
export const ITEM_RESPONDIDO: Partial<Record<TipoDaMensagem, TipoDaMensagem>> = {
  resposta_ao_pedido: 'pedido_de_material',
  resposta_ao_questionamento: 'questionamento',
  resposta_ao_apontamento: 'apontamento',
};

/** O código da recusa do servidor; `indisponivel` é falha de rede ou do servidor. */
export type RecusaDaConversa =
  | 'TIPO_NAO_CABE_AO_LADO'
  | 'ITEM_NAO_ENCONTRADO'
  | 'ITEM_JA_RESPONDIDO'
  | 'SEM_ENTREGA'
  | 'TEXTO_OBRIGATORIO'
  | 'MATERIAL_E_MOTIVO'
  | 'PRAZO_NO_PASSADO'
  | 'CAMPO_APONTADO_OBRIGATORIO'
  | 'DESFECHO_INVALIDO'
  | 'MOTIVO_OBRIGATORIO'
  | 'RECEBIMENTO_NAO_CONCLUIDO'
  | 'RECEBIMENTO_NAO_CABE'
  | 'CASE_NOT_FOUND'
  | 'indisponivel';

const RECUSAS: ReadonlySet<string> = new Set<RecusaDaConversa>([
  'TIPO_NAO_CABE_AO_LADO',
  'ITEM_NAO_ENCONTRADO',
  'ITEM_JA_RESPONDIDO',
  'SEM_ENTREGA',
  'TEXTO_OBRIGATORIO',
  'MATERIAL_E_MOTIVO',
  'PRAZO_NO_PASSADO',
  'CAMPO_APONTADO_OBRIGATORIO',
  'DESFECHO_INVALIDO',
  'MOTIVO_OBRIGATORIO',
  'RECEBIMENTO_NAO_CONCLUIDO',
  'RECEBIMENTO_NAO_CABE',
  'CASE_NOT_FOUND',
]);

export class ConversaError extends Error {
  public readonly recusa: RecusaDaConversa;

  constructor(recusa: RecusaDaConversa) {
    super(recusa);
    this.recusa = recusa;
    this.name = 'ConversaError';
  }
}

function recusaDoErro(err: unknown): RecusaDaConversa {
  if (!axios.isAxiosError(err)) return 'indisponivel';
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return typeof detalhe === 'string' && RECUSAS.has(detalhe)
    ? (detalhe as RecusaDaConversa)
    : 'indisponivel';
}

export async function lerConversa(
  client: AxiosInstance,
  caseId: string,
): Promise<ConversaDoCasoData> {
  try {
    const { data } = await client.get<ConversaDoCasoData>(`/casos/${caseId}/conversa`);
    return data;
  } catch (err) {
    throw new ConversaError(recusaDoErro(err));
  }
}

export async function enviarMensagem(
  client: AxiosInstance,
  caseId: string,
  entrada: MensagemEntradaData,
): Promise<MensagemData> {
  try {
    const { data } = await client.post<MensagemData>(`/casos/${caseId}/conversa`, entrada);
    return data;
  } catch (err) {
    throw new ConversaError(recusaDoErro(err));
  }
}

/** Os itens que seguem abertos de um tipo, na ordem da conversa. */
export function itensAbertos(conversa: ConversaDoCasoData, tipo: TipoDaMensagem): MensagemData[] {
  return conversa.mensagens.filter((m) => m.tipo === tipo && m.situacao === 'aberto');
}
